import { useEffect, useState } from 'react';
import { useQuery } from '../../../hooks';
import useQueryStringSync from '../../../hooks/useQueryStringSync';
import { getUsers } from '../../../utils/api/users';
import { User } from '../../../interfaces/User';
import { castArrayOfPropsToQueryObject, extractQueryArrayValues } from '../../../utils/arrayParamInQuery';
import { getClientIDs } from '../../../utils/getClientIDsByFreeText';
import { useEnumsContext } from '../../../context/EnumsContext';
import globalErrorHandler from '../../../utils/globalErrorHandler';
import { set } from 'date-fns';
import { InsurancePolywizzStatuses, FamilyMemberType } from '../../../interfaces/Insurance';

const useFilterConfigsForPolywizz = () => {
    const queryParams = useQuery();

    // client IDs
    const [clientIds, setClientIds] = useState<string[]>(extractQueryArrayValues('clientIds', (value: string) => queryParams.get(value)));
    const clientIdsObj = castArrayOfPropsToQueryObject('clientIds', clientIds);

    // filters
    const [page, setPage] = useState(Number(queryParams.get('page') || 0));
    const [sortBy, setSortBy] = useState(queryParams.get('sortBy') || '-createdAt');
    const [clientName, setClientName] = useState(queryParams.get('clientName') || '');
    const [status, setStatus] = useState(queryParams.get('status') || '');
    const [familyMemberType, setFamilyMemberType] = useState(queryParams.get('familyMemberType') || '');
    const [createdAt, setCreatedAt] = useState({
        start: queryParams.get('createdAtStart') || '',
        end: queryParams.get('createdAtEnd') || '',
    });
    const [policiesSubmissionTime, setPoliciesSubmissionTime] = useState({
        start: queryParams.get('policiesSubmissionTimeStart') || '',
        end: queryParams.get('policiesSubmissionTimeEnd') || '',
    });

    const [submissionTime, setSubmissionTime] = useState({
        start: queryParams.get('submissionTimeStart') || '',
        end: queryParams.get('submissionTimeEnd') || '',
    });

    // helper states
    const [statuses, setStatuses] = useState<string[]>([]);

    // options for select inputs
    const enums = useEnumsContext();
    useEffect(() => {
        const statuses: string[] = Object.values(InsurancePolywizzStatuses);
        setStatuses(statuses);
    }, []);

    const nonEmptyFilters = {
        limit: MAX_ITEMS_PER_PAGE,
        ...(clientIdsObj && clientIdsObj),
        ...(page !== undefined && { page }),
        ...(sortBy && { sortBy }),
        ...(clientName && { clientName }),
        ...(status && { status }),
        ...(familyMemberType && { familyMemberType }),
        ...(createdAt.start && { createdAtStart: createdAt.start }),
        ...(createdAt.end && { createdAtEnd: createdAt.end }),
        ...(policiesSubmissionTime.start && { policiesSubmissionTimeStart: policiesSubmissionTime.start }),
        ...(policiesSubmissionTime.end && { policiesSubmissionTimeEnd: policiesSubmissionTime.end }),
        ...(submissionTime.start && {
            submissionTimeStart: submissionTime.start,
        }),
        ...(submissionTime.end && { submissionTimeEnd: submissionTime.end }),
    };

    useQueryStringSync(nonEmptyFilters, false);

    const onSortChanged = (newSortBy: string) => {
        if (newSortBy === sortBy) {
            if (newSortBy[0] === '-') {
                newSortBy = newSortBy.slice(1);
            } else {
                newSortBy = `-${newSortBy}`;
            }
        }
        setSortBy(newSortBy);
    };

    const handleFreeSearch = async (freeText: string) => {
        if (!freeText) {
            setClientIds([]);
            return;
        }
        try {
            const clientIds = await getClientIDs(freeText);
            console.log('clientIDs', clientIds);

            setClientIds(clientIds);
        } catch (error) {
            globalErrorHandler(error);
        }
    };

    const handleSelectClientInSearch = (clientId: string) => {
        setClientIds(clientId ? [clientId] : []);
    };

    const statusChanged = (status: string) => {
        setPage(0);
        setStatus(status);
    };

    const familyMemberTypeChanged = (familyMemberType: string) => {
        setPage(0);
        setFamilyMemberType(familyMemberType);
    };

    const dataForForms = [
        {
            label: 'Status',
            type: 'select',
            value: { label: status, value: status },
            name: 'moduleStatus',
            options: [{ label: 'All', value: '' }].concat(statuses.map((s: any) => ({ label: s, value: s }))),
            onChange: (item: any) => {
                setPage(0);
                statusChanged(item.value);
            },
        },
        {
            label: 'Family Member Type',
            type: 'select',
            value: { label: familyMemberType, value: familyMemberType },
            name: 'familyMemberType',
            options: [
                { label: 'All', value: '' },
                { label: 'Client', value: FamilyMemberType.client },
                { label: 'Spouse', value: FamilyMemberType.spouse },
            ],
            onChange: (item: any) => {
                setPage(0);
                familyMemberTypeChanged(item.value);
            },
        },
        {
            label: 'Submission Time',
            type: 'dateRange',
            value: { start: submissionTime.start, end: submissionTime.end },
            name: 'submissionTime',
            placeholder: 'Select Date Range',
            onChange: (e: any) => {
                setPage(0);
                dateRangeSetter(setSubmissionTime, e);
            },
            onClean: () => {
                setPage(0);
                setSubmissionTime({ start: '', end: '' });
            },
        },
        {
            label: 'Created At',
            type: 'dateRange',
            value: { start: createdAt.start, end: createdAt.end },
            name: 'createdAt',
            placeholder: 'Select Date Range',
            onChange: (e: any) => {
                setPage(0);
                dateRangeSetter(setCreatedAt, e);
            },
            onClean: () => {
                setPage(0);
                setCreatedAt({ start: '', end: '' });
            },
        },
        {
            label: 'Policies Submission Time',
            type: 'dateRange',
            value: { start: policiesSubmissionTime.start, end: policiesSubmissionTime.end },
            name: 'policiesSubmissionTime',
            placeholder: 'Select Date Range',
            onChange: (e: any) => {
                setPage(0);
                dateRangeSetter(setPoliciesSubmissionTime, e);
            },
            onClean: () => {
                setPage(0);
                setPoliciesSubmissionTime({ start: '', end: '' });
            },
        },
    ];

    return {
        page,
        setPage,
        sortBy,
        setSortBy,
        clientName,
        setClientName,
        familyMemberType,
        setFamilyMemberType,
        status,
        setStatus,
        dataForForms,
        onSortChanged,
        handleFreeSearch,
        handleSelectClientInSearch,
        policiesSubmissionTime,
        setPoliciesSubmissionTime,
        submissionTime,
        setSubmissionTime,
        statuses,
        MAX_ITEMS_PER_PAGE,
    };
};

const MAX_ITEMS_PER_PAGE = 20;
const dateRangeSetter = (setter: any, e: any) => {
    if (!e) {
        setter({ start: '', end: '' });
        return;
    }
    const [start, end] = e;
    setter({ start, end });
};

export default useFilterConfigsForPolywizz;
